import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  makeStyles,
  Typography,
  Divider,
  Box,
  Chip
} from "@material-ui/core";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import * as yup from "yup";
import FormDatePicker from "Controls/DatePicker";
import FormInput from "Controls/Input";
import FormCheckBox from "Controls/Checkbox";
import FormSelect from "Controls/Select";
import moment from "moment";
import DnerhsApi from "../../../api/DnerhsApi";

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: '700px',
      maxWidth: '800px',
    }
  },
  title: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(2),
  },
  content: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  infoBox: {
    backgroundColor: '#f8f9fa',
    padding: theme.spacing(2),
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
  warningBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    padding: theme.spacing(2),
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
  periodBox: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: '#fff',
  },
  periodMeta: {
    color: '#6c757d',
    fontSize: '0.8rem',
  }
}));

const ESTADOS_CONVENIO = [
  { id: "Convenio en proceso de creación por el responsable", label: "En proceso de creación" },
  { id: "Convenio pendiente de revisión por DNERHS", label: "Pendiente de revisión" },
  { id: "Convenio con correcciones solicitadas por DNERHS", label: "Con correcciones solicitadas" },
  { id: "Convenio pendiente de firma", label: "Pendiente de firma" },
  { id: "Convenio firmado", label: "Firmado" },
  { id: "Convenio anulado", label: "Anulado" },
  { id: "Convenio archivado", label: "Archivado" }
];

const validationSchema = yup.object().shape({
  estado: yup.string().required("El estado es requerido"),
  fechaFirma: yup.date().nullable(),
  fechaInicioVigencia: yup.date().nullable(),
  fechaFinVigencia: yup.date().nullable()
    .test('is-after-start', 'Debe ser posterior a la fecha de inicio', function(value) {
      const { fechaInicioVigencia } = this.parent;
      if (!value || !fechaInicioVigencia) return true;
      return moment(value).isAfter(moment(fechaInicioVigencia));
    }),
  numeroResolucion: yup.string(),
  observacion: yup.string(),
  renovacionConvenio: yup.boolean()
    .test('at-least-one-renovado', 'Si marca "Es Renovación de Convenio", debe seleccionar al menos un Renovado con su fecha', function(value) {
      if (!value) return true; // Si no está marcado, no validar

      const { renovado1, renovado2, renovado3, fechaRenovado1, fechaRenovado2, fechaRenovado3 } = this.parent;

      // Al menos uno debe estar marcado Y tener fecha
      const tieneRenovado1 = renovado1 && fechaRenovado1;
      const tieneRenovado2 = renovado2 && fechaRenovado2;
      const tieneRenovado3 = renovado3 && fechaRenovado3;

      return tieneRenovado1 || tieneRenovado2 || tieneRenovado3;
    })
});

const EditarVigenciaModal = ({ open, onClose, convenio, onSave }) => {
  const classes = useStyles();
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      estado: '',
      fechaFirma: null,
      fechaInicioVigencia: null,
      fechaFinVigencia: null,
      numeroResolucion: '',
      cartaCompromiso: false,
      renovacionConvenio: false,
      renovado1: false,
      renovado2: false,
      renovado3: false,
      fechaRenovado1: null,
      fechaRenovado2: null,
      fechaRenovado3: null,
      observacion: ''
    }
  });

  const { handleSubmit, reset, formState: { errors } } = methods;
  const [periodos, setPeriodos] = useState([]);
  const [loadingPeriodos, setLoadingPeriodos] = useState(false);

  // Observar el valor del checkbox en tiempo real
  const renovacionConvenioValue = useWatch({
    control: methods.control,
    name: "renovacionConvenio",
    defaultValue: false
  });

  console.log('🔍 renovacionConvenioValue:', renovacionConvenioValue);

  // Observar los checkboxes de renovados
  const renovado1Value = useWatch({
    control: methods.control,
    name: "renovado1",
    defaultValue: false
  });

  const renovado2Value = useWatch({
    control: methods.control,
    name: "renovado2",
    defaultValue: false
  });

  const renovado3Value = useWatch({
    control: methods.control,
    name: "renovado3",
    defaultValue: false
  });

  useEffect(() => {
    if (convenio && open) {
      reset({
        estado: convenio.estado || '',
        fechaFirma: convenio.fechaFirma || null,
        fechaInicioVigencia: convenio.fechaInicioVigencia || null,
        fechaFinVigencia: convenio.fechaFinVigencia || null,
        numeroResolucion: convenio.numeroResolucion || '',
        cartaCompromiso: convenio.cartaCompromiso || false,
        renovacionConvenio: convenio.renovacion || false,
        // Inferir checkboxes desde las fechas
        renovado1: convenio.fechaRenovado1 ? true : false,
        renovado2: convenio.fechaRenovado2 ? true : false,
        renovado3: convenio.fechaRenovado3 ? true : false,
        fechaRenovado1: convenio.fechaRenovado1 || null,
        fechaRenovado2: convenio.fechaRenovado2 || null,
        fechaRenovado3: convenio.fechaRenovado3 || null,
        observacion: convenio.observacion || ''
      });
    }
  }, [convenio, open, reset]);

  useEffect(() => {
    const loadPeriodos = async () => {
      if (!convenio?.id || !open) return;
      setLoadingPeriodos(true);
      try {
        const { data } = await DnerhsApi.get(`/convenios/${convenio.id}/periodos`);
        setPeriodos(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error al cargar periodos del convenio:", error);
        setPeriodos([]);
      } finally {
        setLoadingPeriodos(false);
      }
    };

    loadPeriodos();
  }, [convenio, open]);

  // NOTA: NO limpiamos las fechas cuando se desmarcan los checkboxes
  // porque necesitamos mantener el historial de renovaciones.
  // El backend se encargará de determinar el tipo_renovacion basado en las fechas presentes.

  const onSubmit = (data) => {
    // Validación adicional: Si "Es Renovación de Convenio" está marcado,
    // al menos uno de los renovados debe estar marcado con su fecha
    if (data.renovacionConvenio) {
      const tieneRenovado1 = data.renovado1 && data.fechaRenovado1;
      const tieneRenovado2 = data.renovado2 && data.fechaRenovado2;
      const tieneRenovado3 = data.renovado3 && data.fechaRenovado3;

      if (!tieneRenovado1 && !tieneRenovado2 && !tieneRenovado3) {
        alert('⚠️ Si marca "Es Renovación de Convenio", debe seleccionar al menos un Renovado (1, 2 o 3) con su respectiva fecha.');
        return;
      }
    }

    // Formatear fechas
    const formattedData = {
      estado: data.estado,
      fechaFirma: data.fechaFirma ? moment(data.fechaFirma).format('YYYY-MM-DD') : null,
      fechaInicioVigencia: data.fechaInicioVigencia ? moment(data.fechaInicioVigencia).format('YYYY-MM-DD') : null,
      fechaFinVigencia: data.fechaFinVigencia ? moment(data.fechaFinVigencia).format('YYYY-MM-DD') : null,
      numeroResolucion: data.numeroResolucion || null,
      cartaCompromiso: data.cartaCompromiso || false,
      renovacionConvenio: data.renovacionConvenio || false,
      observacion: data.observacion || null,
      // IMPORTANTE: Enviar TODAS las fechas de renovado, no solo las que tienen checkbox marcado
      // Esto permite que el backend determine correctamente el tipo_renovacion
      fechaRenovado1: data.fechaRenovado1 ? moment(data.fechaRenovado1).format('YYYY-MM-DD') : null,
      fechaRenovado2: data.fechaRenovado2 ? moment(data.fechaRenovado2).format('YYYY-MM-DD') : null,
      fechaRenovado3: data.fechaRenovado3 ? moment(data.fechaRenovado3).format('YYYY-MM-DD') : null
    };

    console.log('📤 Datos del formulario (data):', data);
    console.log('📤 Datos formateados a enviar:', formattedData);
    console.log('📤 fechaRenovado1 original:', data.fechaRenovado1);
    console.log('📤 fechaRenovado2 original:', data.fechaRenovado2);
    console.log('📤 fechaRenovado3 original:', data.fechaRenovado3);
    onSave(formattedData);
  };

  const formatDate = (value) => {
    return value ? moment(value).format('DD/MM/YYYY') : 'Sin fecha';
  };

  if (!convenio) return null;

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} maxWidth="md">
      <DialogTitle className={classes.title}>
        <Typography variant="h6">
          Editar Datos de Vigencia - Convenio #{convenio.id}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <FormProvider {...methods}>
          <form>
            {/* Información del Convenio */}
            <Box className={classes.infoBox}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Institución:</strong> {convenio.institucionFormadora?.institucion}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Sede:</strong> {convenio.institucionFormadora?.sede}
              </Typography>
              <Typography variant="subtitle2">
                <strong>Estado Actual:</strong>{' '}
                <Chip
                  label={convenio.estado}
                  size="small"
                  color="primary"
                  style={{ marginLeft: 8 }}
                />
              </Typography>
            </Box>

            {/* Advertencia */}
            <Box className={classes.warningBox}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                ⚠️ Advertencia: Esta acción modificará datos críticos del convenio.
                Se recomienda ingresar observaciones sobre la modificación.
              </Typography>
            </Box>

            <Divider style={{ margin: '16px 0' }} />

            {/* Sección: Estado */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                Periodos registrados
              </Typography>
              {loadingPeriodos && (
                <Typography variant="body2" className={classes.periodMeta}>
                  Cargando periodos...
                </Typography>
              )}
              {!loadingPeriodos && periodos.length === 0 && (
                <Typography variant="body2" className={classes.periodMeta}>
                  Todavia no hay periodos registrados en la tabla historica.
                </Typography>
              )}
              {!loadingPeriodos && periodos.map((periodo) => (
                <Box key={periodo.id} className={classes.periodBox}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Chip
                        size="small"
                        label={`${periodo.tipoPeriodo || 'Periodo'} ${periodo.numeroPeriodo !== null && periodo.numeroPeriodo !== undefined ? periodo.numeroPeriodo : ''}`}
                        color={periodo.tipoPeriodo === 'RENOVACION' ? 'primary' : 'default'}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2">
                        <strong>Estado:</strong> {periodo.estado || 'Sin estado'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Typography variant="body2" className={classes.periodMeta}>
                        Vigencia: {formatDate(periodo.fechaInicioVigencia)} - {formatDate(periodo.fechaFinVigencia)}
                      </Typography>
                      <Typography variant="body2" className={classes.periodMeta}>
                        Gestion academica: {formatDate(periodo.fechaInicioGestion)} - {formatDate(periodo.fechaFinGestion)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                Estado del Convenio
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormSelect
                    name="estado"
                    label="Estado"
                    options={ESTADOS_CONVENIO}
                    required
                    errorobj={errors}
                  />
                </Grid>
              </Grid>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Sección: Fechas */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                Fechas de Vigencia
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormDatePicker
                    name="fechaFirma"
                    label="Fecha de Firma"
                    errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormDatePicker
                    name="fechaInicioVigencia"
                    label="Fecha Inicio Vigencia"
                    errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormDatePicker
                    name="fechaFinVigencia"
                    label="Fecha Fin Vigencia"
                    errorobj={errors}
                  />
                </Grid>
              </Grid>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Sección: Otros Datos */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                Información Adicional
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormInput
                    name="numeroResolucion"
                    label="Número de Resolución"
                    errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormCheckBox
                    name="cartaCompromiso"
                    label="Presentó Carta de Compromiso"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormCheckBox
                    name="renovacionConvenio"
                    label="Es Renovación de Convenio"
                  />
                  {errors.renovacionConvenio && (
                    <Typography variant="caption" color="error" style={{ marginLeft: '14px', display: 'block' }}>
                      {errors.renovacionConvenio.message}
                    </Typography>
                  )}
                </Grid>

                {/* Campos condicionales: Renovados 1, 2, 3 */}
                {renovacionConvenioValue && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormCheckBox
                        name="renovado1"
                        label="Renovado 1"
                      />
                    </Grid>

                    {renovado1Value && (
                      <Grid item xs={12} md={6}>
                        <FormDatePicker
                          name="fechaRenovado1"
                          label="Fecha Renovado 1"
                          errorobj={errors}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <FormCheckBox
                        name="renovado2"
                        label="Renovado 2"
                      />
                    </Grid>

                    {renovado2Value && (
                      <Grid item xs={12} md={6}>
                        <FormDatePicker
                          name="fechaRenovado2"
                          label="Fecha Renovado 2"
                          errorobj={errors}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <FormCheckBox
                        name="renovado3"
                        label="Renovado 3"
                      />
                    </Grid>

                    {renovado3Value && (
                      <Grid item xs={12} md={6}>
                        <FormDatePicker
                          name="fechaRenovado3"
                          label="Fecha Renovado 3"
                          errorobj={errors}
                        />
                      </Grid>
                    )}
                  </>
                )}

                <Grid item xs={12}>
                  <FormInput
                    name="observacion"
                    label="Observaciones / Motivo de Modificación"
                    multiline
                    rows={4}
                    errorobj={errors}
                    placeholder="Ej: Corrección de fecha por error de carga, Cambio de resolución ministerial, etc."
                  />
                </Grid>
              </Grid>
            </div>
          </form>
        </FormProvider>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px', backgroundColor: '#f8f9fa' }}>
        <Button onClick={onClose} color="default" variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          variant="contained"
          style={{ marginLeft: 8 }}
        >
          Guardar Cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditarVigenciaModal;
